import { PushNotifications } from "@capacitor/push-notifications";
import { getNativePlatform, isNativeApp } from "@/lib/runtime";
import {
  subscribeNativePush,
  unsubscribeNativePush as unsubscribeNativePushApi,
} from "@/api/account";
import { getOrCreateInstallationId } from "@/lib/installationId";

const NATIVE_TOKEN_KEY = "lapstr:native-push-token";

let listenersPromise;

function getInstallationId() {
  return getOrCreateInstallationId();
}

async function persistToken(token) {
  if (!token || !localStorage.getItem("speedway_token")) return;
  const platform = getNativePlatform();
  await subscribeNativePush({
    token,
    installation_id: getInstallationId(),
    platform,
  });
  localStorage.setItem(NATIVE_TOKEN_KEY, token);
}

function notificationDeepLink(notification) {
  return notification?.data?.deeplink || notification?.data?.link || notification?.link || null;
}

function installListeners() {
  if (!isNativeApp()) return Promise.resolve();
  if (listenersPromise) return listenersPromise;

  listenersPromise = Promise.all([
    PushNotifications.addListener("registration", ({ value }) => {
      void persistToken(value).catch((error) => {
        console.error("[PUSH] Kunde inte spara native push-token", error);
      });
    }),
    PushNotifications.addListener("registrationError", (error) => {
      console.error("[PUSH] Native registrering misslyckades", error);
    }),
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      window.dispatchEvent(new CustomEvent("native:push-received", {
        detail: { notification },
      }));
    }),
    PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
      const deeplink = notificationDeepLink(notification);
      if (!deeplink) return;
      window.dispatchEvent(new CustomEvent("native:push-deeplink", {
        detail: { deeplink },
      }));
    }),
  ]);
  return listenersPromise;
}

export async function syncNativePushRegistration() {
  if (!isNativeApp()) return false;
  await installListeners();
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive !== "granted") return false;
  await PushNotifications.register();
  return true;
}

export async function getNativePushPermission() {
  if (!isNativeApp()) return "default";
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === "granted") return "granted";
  if (permission.receive === "denied") return "denied";
  return "default";
}

export async function ensureNativePushSubscribed() {
  if (!isNativeApp()) return false;
  await installListeners();

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== "granted") {
    throw new Error("Du behöver tillåta notiser i enhetens inställningar.");
  }

  await PushNotifications.register();
  return true;
}

export async function ensureNativePushUnsubscribed() {
  if (!isNativeApp()) return;
  const installationId = getInstallationId();
  try {
    await unsubscribeNativePushApi({
      installation_id: installationId,
      platform: getNativePlatform(),
    });
  } finally {
    localStorage.removeItem(NATIVE_TOKEN_KEY);
    await PushNotifications.unregister();
  }
}

export function unregisterNativePushForLogout() {
  if (!isNativeApp() || !localStorage.getItem("speedway_token")) return;
  const installationId = getInstallationId();
  void unsubscribeNativePushApi({
    installation_id: installationId,
    platform: getNativePlatform(),
  }).catch(() => {});
  localStorage.removeItem(NATIVE_TOKEN_KEY);
}
